'use client';

import { useState, useEffect, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  History,
  ArrowRight,
  LogIn,
  ShieldAlert,
  Info,
  HelpCircle,
  Lock,
  ExternalLink,
  ClipboardCheck,
  Smartphone,
  Store,
  ChevronDown,
} from 'lucide-react';
import axios from 'axios';
import Link from 'next/link';
import { VirtualCardDisplay } from '../components/VirtualCardDisplay';

const Navbar = dynamic(() => import('@/app/components/Navbar'), { ssr: false });
const Footer = dynamic(() => import('@/app/components/Footer'), { ssr: false });

const getApiUrl = () => {
  const configuredUrl = process.env.NEXT_PUBLIC_API_URL;
  if (configuredUrl) return configuredUrl;
  if (typeof window !== 'undefined') return `${window.location.origin}/api`;
  return 'http://localhost:3000/api';
};

// Configurable Payment Details
const EZCASH_NUMBER = process.env.NEXT_PUBLIC_EZCASH_NUMBER || '0767564233';
const EZCASH_HOLDER = process.env.NEXT_PUBLIC_EZCASH_HOLDER || 'Baasil Topup';

const translations = {
  en: {
    totalBalance: 'Total Balance',
    title: 'eZ Cash Wallet',
    subtitleRedeem: 'Redeem',
    tagline: 'Instantly claim BCoins to your wallet',
    loginRequired: 'Login Required',
    loginDesc: 'Please login to redeem your EzCash reference numbers and view your BCoins balance.',
    loginBtn: 'Login Now',
    retrievingDetails: 'Retrieving details...',
    sendPaymentTo: 'Step 1: Send eZ Cash Payment',
    copyCode: 'Copy Number',
    copied: 'Copied',
    transactionRn: 'Step 3: Submit 14-Digit Reference (RN)',
    placeholderRn: 'e.g. 20260308XXXXXX',
    enterRnHelp: 'Enter the exact 14-digit RN from your eZ Cash SMS (numbers only).',
    pasteClipboard: 'Paste',
    digitsInfo: 'Must be 14 digits',
    redeemBtn: 'CLAIM BCOINS NOW',
    verifying: 'Claiming BCoins...',
    redemptionSuccess: 'Redemption Success!',
    redemptionFailed: 'Redemption Failed',
    bcoinsCredited: 'BCoins Credited',
    rulesTitle: 'Rules & Guide',
    rulesSubtitle: 'How to claim BCoins instantly via eZ Cash',
    goldenRuleTitle: 'The Golden Rule: Your 14-Digit RN',
    goldenRuleDesc: 'To credit your BCoins wallet, we require the exact 14-digit Reference Number (RN) generated by eZ Cash. It usually begins with the current year (e.g. 2026...).',
    goldenRuleSiNote: '(ඔබගේ ගිණුමට මුදල් බැර කිරීමට, eZ Cash මඟින් ලැබෙන ඉලක්කම් 14කින් යුත් Reference Number එක අනිවාර්ය වේ).',
    rule1Title: 'Dialog Genie App & SMS',
    rule1Desc: 'Retrieve the 14-digit Reference Number (RN) from the eZ Cash SMS notification or by checking the Genie app statement history.',
    rule2Title: 'Communication Shops',
    rule2Desc: 'Always verify with the cashier before making transaction: "Will the Dialog eZ Cash ticket provide a 14-digit reference RN code?". If not, cancel payment.',
    rule3Title: 'Service Processing Fee',
    rule3Desc: 'The eZ Cash mobile network automatically applies a standard Rs. 20 processing fee per transaction (deducted from LKR).',
    historyTitle: 'Redemption History',
    historySubtitle: 'Your historical eZ Cash claims logs',
    noHistory: 'No redemptions claimed yet. Enter a reference number above to get started.',
    bcoins: 'BCoins',
    helpTitle: 'Frequently Asked Questions',
    close: 'Close',
    supportLink: 'Contact Support via WhatsApp',
    loginRequiredToRedeem: 'Please login first to redeem BCoins',
    errorFailedToRedeem: 'Failed to redeem reference number',
    faq: [
      {
        q: 'Where is the 14-digit reference number (RN)?',
        a: 'After transferring money, you will receive an SMS confirmation from Dialog eZ Cash. Look for the field "RN: 2026XXXXXXXXXX". You can also look in your Dialog Genie app\'s transaction statement.',
      },
      {
        q: 'How long does the redemption take?',
        a: 'It is instant! If the 14-digit reference number is valid and matches our transaction logs, the BCoins balance is credited to your wallet in real-time within 2 to 5 seconds.',
      },
      {
        q: 'Why did my redemption fail?',
        a: 'Failures are usually caused by: (1) Typo in the 14-digit code, (2) The transaction was completed using a method that doesn\'t generate a valid eZ Cash RN, (3) The transaction code has already been redeemed.',
      },
    ],
  },
  si: {
    totalBalance: 'මුළු ශේෂය',
    title: 'eZ Cash පසුම්බිය',
    subtitleRedeem: 'මුදල් බැර කරන්න',
    tagline: 'eZ Cash ගනුදෙනු මඟින් ක්ෂණිකව BCoins ලබාගන්න',
    loginRequired: 'ප්‍රවිෂ්ට වීම අවශ්‍යයි',
    loginDesc: 'ඔබගේ BCoins ශේෂය බැලීමට සහ මුදල් ලබාගැනීමට කරුණාකර ප්‍රථමයෙන් ගිණුමට ප්‍රවිෂ්ට වන්න.',
    loginBtn: 'ප්‍රවිෂ්ට වන්න',
    retrievingDetails: 'තොරතුරු ලබා ගනිමින්...',
    sendPaymentTo: 'පියවර 1: eZ Cash මුදල් එවන්න',
    copyCode: 'අංකය පිටපත් කරන්න',
    copied: 'පිටපත් කලා',
    transactionRn: 'පියවර 3: ඉලක්කම් 14 යොමු අංකය (RN)',
    placeholderRn: 'උදා: 20260308XXXXXX',
    enterRnHelp: 'ඔබගේ eZ Cash SMS හි තිබෙන සම්පූර්ණ ඉලක්කම් 14ක RN අංකය ඇතුලත් කරන්න (සංඛ්‍යාවන් පමණයි).',
    pasteClipboard: 'පේස්ට් කරන්න',
    digitsInfo: 'ඉලක්කම් 14ක් විය යුතුය',
    redeemBtn: 'දැන්ම BCoins ලබාගන්න',
    verifying: 'පරීක්ෂා කරමින්...',
    redemptionSuccess: 'මුදල් බැරවීම සාර්ථකයි!',
    redemptionFailed: 'මුදල් බැරවීම අසාර්ථකයි',
    bcoinsCredited: 'BCoins බැර කලා',
    rulesTitle: 'නීති සහ උපදෙස්',
    rulesSubtitle: 'පහසුවෙන් ගිණුම ටොප්-අප් කරගැනීමට මෙය කියවන්න',
    goldenRuleTitle: 'ප්‍රධාන රීතිය: ඔබගේ ඉලක්කම් 14ක යොමු අංකය (RN)',
    goldenRuleDesc: 'ඔබගේ BCoins පසුම්බියට මුදල් බැර කිරීමට, eZ Cash මඟින් ලැබෙන නිවැරදි ඉලක්කම් 14කින් යුත් Reference Number එක (RN) අනිවාර්ය වේ. එය සාමාන්‍යයෙන් වත්මන් වසරෙන් ආරම්භ වේ (උදා: 2026...).',
    goldenRuleSiNote: '',
    rule1Title: 'Dialog Genie App සහ SMS කෙටි පණිවිඩ',
    rule1Desc: 'eZ Cash SMS මඟින් හෝ Genie App ගනුදෙනු ඉතිහාසය පරීක්ෂා කිරීමෙන් ඉලක්කම් 14ක යොමු අංකය (RN) ලබා ගන්න.',
    rule2Title: 'සන්නිවේදන සාප්පු (Communications)',
    rule2Desc: 'ගනුදෙනුව සිදු කිරීමට පෙර සැමවිටම කැෂියර්වරයාගෙන් විමසන්න: "Dialog eZ Cash ටිකට් පතෙහි ඉලක්කම් 14ක යොමු අංකය (RN) සඳහන් වෙනවාද?". එසේ නොමැති නම් ගෙවීම් නොකරන්න.',
    rule3Title: 'සේවා ගාස්තු (Processing Fee)',
    rule3Desc: 'Dialog ජාලය මඟින් එක් ගනුදෙනුවක් සඳහා රු. 20 ක සම්මත සේවා ගාස්තුවක් අය කරනු ලැබේ.',
    historyTitle: 'බැර කිරීම් ඉතිහාසය',
    historySubtitle: 'ඔබගේ පෙර බැර කිරීම් විස්තර',
    noHistory: 'තවම කිසිදු බැර කිරීමක් සිදු කර නැත. ආරම්භ කිරීමට ඉහත යොමු අංකය ඇතුලත් කරන්න.',
    bcoins: 'BCoins',
    helpTitle: 'නිතර අසන ප්‍රශ්න',
    close: 'වසා දමන්න',
    supportLink: 'WhatsApp මඟින් සහාය ලබාගන්න',
    loginRequiredToRedeem: 'මුදල් ලබාගැනීමට කරුණාකර ප්‍රථමයෙන් ගිණුමට ප්‍රවිෂ්ට වන්න',
    errorFailedToRedeem: 'යොමු අංකය පරීක්ෂා කිරීම අසාර්ථකයි',
    faq: [
      {
        q: 'ඉලක්කම් 14ක යොමු අංකය (RN) සොයාගන්නේ කොහෙන්ද?',
        a: 'මුදල් මාරු කිරීමෙන් පසු Dialog eZ Cash වෙතින් SMS තහවුරු කිරීමක් ලැබෙනු ඇත. එහි "RN: 2026XXXXXXXXXX" ලෙස ඇති අංකය බලන්න. Genie App හි ප්‍රකාශනයෙන්ද මෙය සොයාගත හැක.',
      },
      {
        q: 'මුදල් බැර වීමට කොපමණ වේලාවක් ගතවේද?',
        a: 'එය ක්ෂණිකයි! ඉලක්කම් 14 යොමු අංකය නිවැරදි නම්, තත්පර 2 සිට 5 දක්වා කාලය තුළ BCoins ඔබගේ පසුම්බියට බැර වේ.',
      },
      {
        q: 'බැර කිරීම අසාර්ථක වීමට හේතු මොනවාද?',
        a: 'ප්‍රධාන හේතු: (1) ඉලක්කම් 14 කේතයේ වැරදි තිබීම, (2) යොමු අංකයක් නොලැබෙන ක්‍රමයකින් මුදල් ගෙවීම, (3) මෙම කේතය මීට පෙර භාවිතා කර තිබීම.',
      },
    ],
  },
  ta: {
    totalBalance: 'மொத்த நிலுவை',
    title: 'eZ Cash வாலட்',
    subtitleRedeem: 'பணம் பெறுக',
    tagline: 'உங்கள் eZ Cash பணத்தை BCoins ஆக உங்கள் வாலட்டில் உடனடியாகப் பெறுங்கள்',
    loginRequired: 'உள்நுழைய வேண்டும்',
    loginDesc: 'உங்கள் BCoins நிலுவைத்தொகையைப் பார்க்கவும் பணம் பெறவும் தயவுசெய்து முதலில் உள்நுழையவும்.',
    loginBtn: 'உள்நுழைக',
    retrievingDetails: 'தகவல்கள் பெறப்படுகின்றன...',
    sendPaymentTo: 'படி 1: eZ Cash பணம் அனுப்பவும்',
    copyCode: 'நகலெடுக்க',
    copied: 'நகலெடுக்கப்பட்டது',
    transactionRn: 'படி 3: 14 இலக்க குறிப்பு எண் (RN)',
    placeholderRn: 'உதாரணம்: 20260308XXXXXX',
    enterRnHelp: 'உங்கள் eZ Cash SMS-இலிருந்து சரியான 14 இலக்க RN எண்ணை உள்ளிடவும் (எண்கள் மட்டுமே).',
    pasteClipboard: 'ஒட்டுக',
    digitsInfo: '14 இலக்கங்கள் வேண்டும்',
    redeemBtn: 'உடனடியாக BCoins பெறுக',
    verifying: 'சரிபார்க்கப்படுகிறது...',
    redemptionSuccess: 'பணம் பெறுதல் வெற்றிகரமாக முடிந்தது!',
    redemptionFailed: 'பணம் பெறுதல் தோல்வியடைந்தது',
    bcoinsCredited: 'BCoins வரவு வைக்கப்பட்டது',
    rulesTitle: 'விதிமுறைகள் மற்றும் வழிகாட்டி',
    rulesSubtitle: 'வாலட்டை உடனடியாக டாப்-அப் செய்வதற்கான விதிமுறைகள்',
    goldenRuleTitle: 'முக்கிய விதி: உங்களின் 14 குறிப்பு எண் (RN)',
    goldenRuleDesc: 'உங்கள் BCoins வாலட்டில் வரவு வைக்க, eZ Cash மூலம் வழங்கப்படும் சரியான 14 குறிப்பு எண் (RN) அவசியமாகும். இது பொதுவாக நடப்பு ஆண்டைக் கொண்டு தொடங்கும் (எ.கா. 2026...).',
    goldenRuleSiNote: '',
    rule1Title: 'Dialog Genie App மற்றும் SMS',
    rule1Desc: 'eZ Cash SMS அறிவிப்பு அல்லது Genie App பரிவர்த்தனை வரலாறு மூலம் 14 குறிப்பு எண்ணை (RN) பெற்றுக்கொள்ளுங்கள்.',
    rule2Title: 'தொடர்பு கடைகள் (Communications)',
    rule2Desc: 'பரிவர்த்தனை செய்வதற்கு முன் எப்போதும் கடைக்காரரிடம் சரிபார்க்கவும்: "eZ Cash சீட்டில் 14 குறிப்பு எண் (RN) இருக்குமா?". இல்லை என்றால் பணம் செலுத்த வேண்டாம்.',
    rule3Title: 'சேவை கட்டணம் (Processing Fee)',
    rule3Desc: 'ஒரு பரிவர்த்தனைக்கு ரூ. 20 சேவை கட்டணம் Dialog நெட்வொர்க் மூலம் தானாகவே கழிக்கப்படும்.',
    historyTitle: 'வரலாறு',
    historySubtitle: 'உங்கள் முந்தைய வரவு விவரங்கள்',
    noHistory: 'இன்னும் பரிவர்த்தனைகள் எதுவும் செய்யப்படவில்லை. குறிப்பு எண்ணை உள்ளிட்டு தொடங்கவும்.',
    bcoins: 'BCoins',
    helpTitle: 'அடிக்கடி கேட்கப்படும் கேள்விகள்',
    close: 'மூடுக',
    supportLink: 'WhatsApp மூலம் ஆதரவை நாடுங்கள்',
    loginRequiredToRedeem: 'டாப்-அப் செய்ய தயவுசெய்து முதலில் உள்நுழையவும்',
    errorFailedToRedeem: 'குறிப்பு எண்ணை சரிபார்ப்பது தோல்வியடைந்தது',
    faq: [
      {
        q: '14 இலக்க குறிப்பு எண் (RN) எங்கே இருக்கும்?',
        a: 'பணம் அனுப்பிய பிறகு Dialog eZ Cash இல் இருந்து ஒரு SMS தஹவல் வரும். அதில் "RN: 2026XXXXXXXXXX" என இருக்கும் எண்ணைப் பாருங்கள். Genie App பரிவர்த்தனை வரலாற்றிலும் இதைப் பார்க்கலாம்.',
      },
      {
        q: 'பணம் வாலட்டில் சேர எவ்வளவு நேரம் ஆகும்?',
        a: 'இது உடனடியாக நடைபெறும்! 14 இலக்க குறிப்பு எண் சரியாக இருந்தால், 2 முதல் 5 வினாடிகளுக்குள் BCoins வரவு வைக்கப்படும்.',
      },
      {
        q: 'பரிவர்த்தனை தோல்வியடைவது ஏன்?',
        a: 'காரணங்கள்: (1) 14 இலக்க குறியீட்டில் உள்ள பிழைகள், (2) குறிப்பு எண் வழங்கப்படாத முறையில் பணம் செலுத்தியது, (3) ஏற்கனவே பயன்படுத்தப்பட்ட குறிப்பு எண்.',
      },
    ],
  },
};

function EzCashContent() {
  const appAuth = useAppAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const langParam = (searchParams.get('lang') as 'en' | 'si' | 'ta') || 'en';
  const t = translations[langParam] || translations.en;

  // States
  const [referenceNumber, setReferenceNumber] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState<{
    success: boolean;
    message: string;
    amount?: number;
  } | null>(null);

  const [balance, setBalance] = useState<number | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [faqExpanded, setFaqExpanded] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const getAuthHeaders = async () => {
    try {
      const token = await appAuth.getToken();
      return token ? { Authorization: `Bearer ${token}` } : null;
    } catch {
      return null;
    }
  };

  const fetchUserData = async () => {
    const headers = await getAuthHeaders();
    if (!headers) {
      setIsLoadingData(false);
      return;
    }
    setBalanceError(null);
    setHistoryError(null);
    try {
      const [balanceRes, historyRes] = await Promise.allSettled([
        axios.get(`${getApiUrl()}/bcoins/balance`, { headers }),
        axios.get(`${getApiUrl()}/bcoins/history`, { headers }),
      ]);

      if (balanceRes.status === 'fulfilled') {
        const nextBalance = balanceRes.value.data?.balance ?? balanceRes.value.data?.data?.balance ?? 0;
        setBalance(typeof nextBalance === 'number' ? nextBalance : Number(nextBalance) || 0);
      } else {
        setBalance(null);
        setBalanceError('Unable to load your current balance.');
      }

      if (historyRes.status === 'fulfilled') {
        const nextHistory = historyRes.value.data?.data ?? historyRes.value.data;
        setHistory(Array.isArray(nextHistory) ? nextHistory : []);
      } else {
        setHistory([]);
        setHistoryError('Unable to load redemption history.');
      }
    } catch (err) {
      console.error('Failed to fetch BCoins data:', err);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [appAuth.isAuthenticated, appAuth.isAuthLoading]);

  const handleRedeem = async () => {
    if (referenceNumber.trim().length !== 14) return;
    setIsRedeeming(true);
    setRedeemResult(null);

    try {
      const headers = await getAuthHeaders();
      if (!headers) {
        setRedeemResult({
          success: false,
          message: t.loginRequiredToRedeem,
        });
        return;
      }

      const res = await axios.post(
        `${getApiUrl()}/bcoins/redeem`,
        { referenceNumber: referenceNumber.trim() },
        { headers },
      );
      setRedeemResult({
        success: true,
        message: res.data?.message || t.bcoinsCredited,
        amount: res.data?.amount,
      });
      setReferenceNumber('');
      fetchUserData();
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        t.errorFailedToRedeem;
      setRedeemResult({ success: false, message: msg });
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(EZCASH_NUMBER);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = text.replace(/\D/g, '').slice(0, 14);
      setReferenceNumber(digits);
    } catch (err) {
      console.error('Failed to read clipboard text:', err);
    }
  };

  const isAuthenticated = appAuth.isAuthenticated && !appAuth.isAuthLoading;

  return (
    <div className="min-h-screen bg-[#F4F8FC] text-slate-800 overflow-x-hidden font-sans">
      <Navbar locale={langParam} />

      {/* Premium Hero Banner (Radial Gradient + Grid Overlay + Clip-path) */}
      <div className="relative min-h-[38vh] md:min-h-[42vh] flex items-center justify-center overflow-hidden pt-28 pb-20">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 46.8% 109%, #BFDEFF 0%, #659EDA 99%)',
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 88%, 50% 100%, 0% 88%)',
          }}
        />

        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(hsla(0,0%,100%,0.15) 1px, transparent 1px), linear-gradient(90deg, hsla(0,0%,100%,0.15) 1px, transparent 1px)',
            backgroundSize: '100px 100px',
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 88%, 50% 100%, 0% 88%)',
          }}
        />

        <div className="relative z-10 text-center space-y-4 max-w-2xl mx-auto px-4 flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/35 bg-white/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm shadow-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            eZ Cash Instant Claim
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-4xl md:text-5xl lg:text-6xl font-display uppercase tracking-tight leading-none text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)] ${
              langParam === 'si' ? 'font-sinhala-title tracking-[0.02em] text-3xl md:text-4xl lg:text-5xl' : langParam === 'ta' ? 'font-tamil' : ''
            }`}
          >
            {langParam === 'si' ? (
              <>eZ Cash <span className="text-white drop-shadow-md">පසුම්බිය</span></>
            ) : langParam === 'ta' ? (
              <>eZ Cash <span className="text-white drop-shadow-md">வாலட்</span></>
            ) : (
              <>eZ Cash <span className="text-white drop-shadow-md">Wallet</span></>
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-xs sm:text-sm font-medium tracking-wide uppercase"
          >
            {t.tagline}
          </motion.p>
        </div>
      </div>

      <main className="max-w-5xl w-full mx-auto px-4 -mt-10 sm:-mt-16 pb-24 relative z-20 flex flex-col items-stretch gap-8">

        {/* Auth / Balance Section */}
        {!isAuthenticated ? (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto w-full border border-slate-200/80 bg-white rounded-2xl p-8 text-center shadow-lg relative overflow-hidden"
          >
            <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-[#5A98D8] to-[#3083E8]" />
            <div className="w-16 h-16 bg-[#5A98D8]/10 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-[#5A98D8]/20">
              <LogIn className="w-8 h-8 text-[#3083E8]" />
            </div>
            <h2 className="text-xl font-bold uppercase tracking-wider mb-2 text-slate-800">{t.loginRequired}</h2>
            <p className="text-slate-500 text-xs leading-relaxed mb-6">
              {t.loginDesc}
            </p>
            <Link
              href="/login"
              id="ezcash-login-btn"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#5A98D8] to-[#3083E8] hover:opacity-90 text-white px-8 py-3.5 rounded-xl font-bold text-xs uppercase tracking-[0.18em] transition-all shadow-md active:scale-95 w-full sm:w-auto"
            >
              {t.loginBtn} <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        ) : isLoadingData ? (
          <div className="flex items-center justify-center py-12 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-[#3083E8]" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">{t.retrievingDetails}</span>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Balance Visual Card */}
            <div className="max-w-md mx-auto w-full">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 100 }}
              >
                <VirtualCardDisplay
                  card={{
                    nickname: t.totalBalance,
                    maskedCardNumber: '•••• •••• •••• 1990',
                    fullCardNumber: `${balance !== null ? balance.toLocaleString() : '0'} BCoins`,
                    expiryDate: `LKR ${balance !== null ? balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}`,
                    cvv: '742',
                    cardHolderName: appAuth.displayName || 'Gamers Wallet',
                    cardBgImage: '/assets/bgcard.png',
                  }}
                  showDetails={true}
                  isActive={true}
                  cardColor="from-[#246fbc] to-[#5A98D8] border border-slate-200/50 shadow-md"
                  onToggleVisibility={() => {}}
                />
              </motion.div>
            </div>

            {/* Error alerts */}
            {(balanceError || historyError) && (
              <div className="max-w-3xl mx-auto rounded-xl border border-red-200 bg-red-50 p-4 text-xs text-red-700 flex items-start gap-2.5 shadow-sm">
                <ShieldAlert className="w-5 h-5 shrink-0 text-red-500" />
                <div>
                  <p className="font-bold uppercase tracking-wider text-red-600">Notice</p>
                  <p className="mt-1 leading-relaxed text-red-700/90">
                    {balanceError} {historyError}
                  </p>
                </div>
              </div>
            )}

            {/* Main Interactive Guide & Claim Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6 items-stretch">
              {/* Step Guide Cards */}
              <div className="flex flex-col gap-4">
                {/* Step 1 */}
                <div className="bg-white border border-slate-200/80 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between shadow-sm">
                  <div className="space-y-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#3083E8]">Step 1</span>
                    <h3 className="text-lg font-bold text-slate-800 uppercase">{t.sendPaymentTo}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Transfer LKR to the following eZ Cash registered mobile number.
                    </p>
                  </div>
                  <div className="mt-5 p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{EZCASH_HOLDER}</p>
                      <p className="text-2xl md:text-3xl font-mono font-black tracking-widest text-slate-800 mt-1">
                        {EZCASH_NUMBER}
                      </p>
                    </div>
                    <button
                      onClick={handleCopy}
                      className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border font-bold text-xs uppercase tracking-widest transition-all ${
                        copied
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                          : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      {copied ? t.copied : t.copyCode}
                    </button>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="bg-white border border-slate-200/80 rounded-2xl p-6 flex items-start gap-4 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#5A98D8]/10 border border-[#5A98D8]/20 text-[#3083E8]">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#3083E8]">Step 2</span>
                    <h4 className="text-sm font-bold text-slate-800 uppercase">Wait for Dialog SMS</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Upon completing the transfer, Dialog eZ Cash will send an SMS to your mobile containing a unique **14-digit Reference Number (RN)**.
                    </p>
                  </div>
                </div>
              </div>

              {/* Claim Form Card */}
              <div className="bg-white border border-slate-200/80 rounded-2xl p-6 md:p-8 flex flex-col justify-between shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-24 h-24 bg-blue-50/50 rounded-full blur-2xl pointer-events-none" />
                <div className="relative z-10 space-y-6">
                  <div className="space-y-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#3083E8]">Step 3</span>
                    <h3 className="text-xl font-bold text-slate-800 uppercase">Submit & Claim BCoins</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Input the 14-digit Reference Number (RN) below to credit BCoins instantly to your wallet.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="relative">
                      <input
                        type="text"
                        value={referenceNumber}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          if (val.length <= 14) setReferenceNumber(val);
                        }}
                        placeholder={t.placeholderRn}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 py-4 px-4 pl-10 text-sm font-mono font-bold tracking-[0.2em] text-slate-800 placeholder:text-slate-300 outline-none transition focus:border-[#3083E8] focus:bg-white focus:ring-4 focus:ring-[#3083E8]/10"
                      />
                      <span className="absolute inset-y-0 left-4 flex items-center text-slate-400 font-mono text-sm">#</span>
                      <button
                        onClick={handlePaste}
                        className="absolute inset-y-0 right-2 my-2 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3.5 text-slate-500 hover:border-[#3083E8]/40 hover:text-[#3083E8] transition"
                      >
                        <ClipboardCheck className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <span className="flex items-center gap-1.5">
                        <Info className="h-3.5 w-3.5 text-[#3083E8]" />
                        {t.enterRnHelp}
                      </span>
                      <span className={`font-bold px-2 py-0.5 rounded ${referenceNumber.length === 14 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}>
                        {referenceNumber.length}/14
                      </span>
                    </div>

                    <button
                      onClick={handleRedeem}
                      disabled={isRedeeming || referenceNumber.length !== 14}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#5A98D8] to-[#3083E8] hover:opacity-90 px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition disabled:bg-slate-100 disabled:from-slate-100 disabled:to-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed shadow-sm"
                    >
                      {isRedeeming ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t.verifying}
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4" />
                          {t.redeemBtn}
                        </>
                      )}
                    </button>

                    {redeemResult && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`rounded-xl border p-4 flex items-start gap-3 ${
                          redeemResult.success
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'border-red-200 bg-red-50 text-red-800'
                        }`}
                      >
                        {redeemResult.success ? (
                          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                        ) : (
                          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                        )}
                        <div className="min-w-0">
                          <h5 className="text-xs font-bold uppercase tracking-[0.08em] text-slate-800">
                            {redeemResult.success ? t.redemptionSuccess : t.redemptionFailed}
                          </h5>
                          <p className="mt-1 text-xs font-mono text-slate-500">
                            {redeemResult.message}
                          </p>
                          {redeemResult.success && redeemResult.amount && (
                            <span className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-3 py-0.5 text-[10px] font-bold font-mono text-emerald-700">
                              +{redeemResult.amount.toLocaleString()} {t.bcoins} credited
                            </span>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Rules and FAQ Accordions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              {/* Rules & Guide Accordion */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 pb-4 mb-4 border-b border-slate-100">
                  <div className="w-10 h-10 rounded-xl bg-[#5A98D8]/10 flex items-center justify-center border border-[#5A98D8]/20 text-[#3083E8]">
                    <ShieldAlert className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800">{t.rulesTitle}</h4>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">{t.rulesSubtitle}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{t.goldenRuleTitle}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{t.goldenRuleDesc}</p>
                    {t.goldenRuleSiNote && (
                      <p className="mt-2 text-[10px] italic text-slate-500 leading-relaxed">{t.goldenRuleSiNote}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    {[
                      { title: t.rule1Title, desc: t.rule1Desc, icon: <Smartphone className="w-4 h-4 text-[#3083E8]" /> },
                      { title: t.rule2Title, desc: t.rule2Desc, icon: <Store className="w-4 h-4 text-[#3083E8]" /> },
                      { title: t.rule3Title, desc: t.rule3Desc, icon: <Info className="w-4 h-4 text-[#3083E8]" /> },
                    ].map((rule, idx) => (
                      <div key={idx} className="flex gap-3 items-start bg-slate-50 border border-slate-200 p-4 rounded-xl">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#5A98D8]/10 border border-[#5A98D8]/15 text-[#3083E8]">
                          {rule.icon}
                        </div>
                        <div>
                          <h6 className="text-[11px] font-bold uppercase tracking-widest text-slate-800">{rule.title}</h6>
                          <p className="mt-1 text-xs leading-relaxed text-slate-500">{rule.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* FAQs Accordion */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 pb-4 mb-4 border-b border-slate-100">
                  <div className="w-10 h-10 rounded-xl bg-[#5A98D8]/10 flex items-center justify-center border border-[#5A98D8]/25 text-[#3083E8]">
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800">{t.helpTitle}</h4>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">General Help & Support</p>
                  </div>
                </div>

                <div className="space-y-2.5">
                  {t.faq.map((faq, idx) => {
                    const isOpen = faqExpanded === idx;
                    return (
                      <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50/50">
                        <button
                          onClick={() => setFaqExpanded(isOpen ? null : idx)}
                          className="w-full text-left px-4 py-3.5 flex items-center justify-between gap-3 text-xs font-bold text-slate-600 hover:text-slate-800 transition"
                        >
                          <span>{faq.q}</span>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180 text-[#3083E8]' : ''}`} />
                        </button>
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: 'auto' }}
                              exit={{ height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 pb-4 pt-1 border-t border-slate-200/60 text-xs text-slate-500 leading-relaxed font-sans">
                                {faq.a}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 text-center font-mono">
                  <Link
                    href="https://wa.me/94742300407"
                    target="_blank"
                    className="inline-flex items-center gap-1.5 text-xs text-[#3083E8] hover:opacity-85 font-bold"
                  >
                    {t.supportLink} <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Redemption History Table */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#5A98D8]/10 flex items-center justify-center border border-[#5A98D8]/20 text-[#3083E8]">
                    <History className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800">{t.historyTitle}</h4>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">{t.historySubtitle}</p>
                  </div>
                </div>
              </div>

              {!Array.isArray(history) || history.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-5 py-12 text-center space-y-3">
                  <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 border border-slate-200 text-slate-400">
                    <History className="h-5 w-5" />
                  </div>
                  <p className="text-xs text-slate-400 uppercase tracking-widest">
                    {t.noHistory}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2.5 max-h-[350px] overflow-y-auto custom-scrollbar pr-1">
                  {history.map((item: any, idx: number) => {
                    const netAmount = Number(item.amount) || 0;
                    return (
                      <div
                        key={idx}
                        className="group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50/50 p-4 transition hover:bg-slate-50 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-600">
                              <CheckCircle2 className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-bold font-mono tracking-widest text-slate-800">
                                RN: {item.referenceNumber}
                              </p>
                              <p className="mt-0.5 text-[9px] uppercase tracking-widest text-slate-400 font-medium">
                                {item.claimedAt
                                  ? new Date(item.claimedAt).toLocaleString()
                                  : new Date(item.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-sm font-bold font-mono text-emerald-600">
                              +Rs.{netAmount.toLocaleString()}
                            </p>
                            <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold font-mono">
                              {t.bcoins}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer locale={langParam} />
    </div>
  );
}

export default function EzCashPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-[#F4F8FC]" style={{ color: '#64748B' }}>
        <Loader2 className="w-6 h-6 animate-spin text-[#3083E8]" />
      </div>
    }>
      <EzCashContent />
    </Suspense>
  );
}
